import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useToast } from '@/hoc';
import { clearVoiceAdmin, getVoiceAdminEmail, useAdminVoiceProvider, useAdminVoiceProviders, useCreateVoiceProvider, useDeleteVoiceProvider, useUpdateVoiceProvider, } from './voice-admin-api';
import { AudioLines, Coins, Cpu, LayoutDashboard, Layers, ListTree, Loader2, Mic, Plus, ShieldCheck, Video, Wallet, } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from '../ui';
import AdminConsoleLayout from './admin-console-layout';
import { DashOverlay } from './admin-ui';
import InterviewModePanel from './interview-mode-panel';
import ProctoringTestPanel from './proctoring-test-panel';
import ProviderDetailView from './provider-detail-view';
import ProviderListPanel from './provider-list-panel';
import ProviderFormModal from './provider-form/provider-form-modal';
import DashboardPanel from './ai-usage/dashboard-panel';
import InterviewDetailPage from './ai-usage/interview-detail-page';
import ModelsPanel from './ai-usage/models-panel';
import PricingPanel from './ai-usage/pricing-panel';
import UsagePanel from './ai-usage/usage-panel';
import VadTestPanel from './vad-test-panel';
import VoiceProviderTestPanel from './voice-provider-test-panel';
const DEFAULT_ROUTE = { view: 'section', key: 'dashboard', org: '' };
const parseRoute = (hash) => {
    var _a;
    const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean).map(decodeURIComponent);
    if (!parts.length)
        return DEFAULT_ROUTE;
    if (parts[0] === 'interview' && parts[1]) {
        // Slugs contain no slash today, but joining the tail is free and means a
        // slug that ever does cannot silently truncate to its first segment.
        return { view: 'interview', slug: parts.slice(1).join('/') };
    }
    return {
        view: 'section',
        key: parts[0],
        org: parts[1] === 'org' ? ((_a = parts[2]) !== null && _a !== void 0 ? _a : '') : '',
    };
};
const routeToHash = (route) => route.view === 'interview'
    ? `#interview/${encodeURIComponent(route.slug)}`
    : `#${route.key}${route.org ? `/org/${encodeURIComponent(route.org)}` : ''}`;
/**
 * Route state kept in sync with the fragment, in both directions.
 *
 * `hashchange` is what makes the browser's own Back button work — without it,
 * Back would change the URL and leave the console rendering the previous screen,
 * which is worse than having no URL state at all.
 */
const useConsoleRoute = () => {
    const [route, setRouteState] = useState(() => parseRoute(typeof window === 'undefined' ? '' : window.location.hash));
    useEffect(() => {
        const onHashChange = () => setRouteState(parseRoute(window.location.hash));
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);
    const setRoute = useCallback((next) => {
        const hash = routeToHash(next);
        if (window.location.hash !== hash) {
            // Assigning the hash pushes a history entry, which is the point: Back
            // steps from an interview to the organization it came from.
            window.location.hash = hash;
        }
        setRouteState(next);
    }, []);
    return [route, setRoute];
};
const SECTIONS = [
    // Grouped so the console reads as two concerns: what the platform SPENDS, and
    // how the platform is CONFIGURED.
    {
        key: 'dashboard',
        label: 'Dashboard',
        description: 'What the platform spends, at a glance — trend, and a chart per dimension.',
        icon: LayoutDashboard,
        group: 'Spend',
    },
    {
        key: 'usage',
        label: 'Usage & Cost',
        description: 'Break the same spend down by any dimension, then drill to the individual calls.',
        icon: Wallet,
        group: 'Spend',
        // Usage is three distinct questions. Stacked on one page you scrolled past
        // two to reach the third; as sub-sections each screen answers one.
        children: [
            {
                key: 'usage.breakdown',
                label: 'Breakdown',
                description: 'Group the same spend by any dimension — feature, model, provider, recruiter.',
                icon: Layers,
            },
            {
                key: 'usage.interviews',
                label: 'Interviews',
                description: 'Every measured interview, most expensive first. Select one for its full breakdown.',
                icon: Video,
            },
            {
                key: 'usage.events',
                label: 'Usage events',
                description: 'One row per API call, newest first, down to the pricing basis of each metric.',
                icon: ListTree,
            },
        ],
    },
    {
        key: 'models',
        label: 'Models',
        description: 'Which model serves each feature, and the registry of models the platform can use.',
        icon: Cpu,
        group: 'Configuration',
    },
    {
        key: 'pricing',
        label: 'Pricing',
        description: 'Provider rate card. Publishing a rate never changes historical cost.',
        icon: Coins,
        group: 'Configuration',
    },
    {
        key: 'providers',
        label: 'Voice Providers',
        description: 'Manage the dynamic STT/TTS providers available when scheduling interviews.',
        icon: Mic,
        group: 'Configuration',
    },
    {
        key: 'interview-mode',
        label: 'Interview Modes',
        description: 'Which engine new interviews are scheduled with. Interviews already scheduled keep their mode.',
        icon: Layers,
        group: 'Configuration',
    },
    {
        key: 'proctoring',
        label: 'Proctoring',
        description: 'Test proctoring face detection and tune the live capture resolution.',
        icon: ShieldCheck,
        group: 'Diagnostics',
    },
    {
        key: 'vad',
        label: 'VAD / Turn Detection',
        description: 'Test the neural VAD (turn detection) against real speech and room noise.',
        icon: AudioLines,
        group: 'Diagnostics',
    },
];
const VoiceProviderManagement = () => {
    const { showToast } = useToast();
    const { data: providers, isLoading } = useAdminVoiceProviders();
    const createMut = useCreateVoiceProvider();
    const updateMut = useUpdateVoiceProvider();
    const deleteMut = useDeleteVoiceProvider();
    const [modal, setModal] = useState({ mode: 'closed' });
    const [confirmDelete, setConfirmDelete] = useState(null);
    // Holds an id, not a row: the list carries summaries and the test panel needs
    // the real spec (endpoint, sample rate) to run against.
    const [testProviderId, setTestProviderId] = useState(null);
    // Spend is what an operator opens this console to see, and the Dashboard
    // answers "how are we doing" without them having to ask a question first.
    const [route, setRoute] = useConsoleRoute();
    const tab = route.view === 'section' ? route.key : 'dashboard';
    const setTab = (key) => setRoute({ view: 'section', key, org: '' });
    // Read-only detail. Previously the ONLY way into a provider's config was the
    // edit form, i.e. a screen where a stray keystroke changes live behaviour.
    const [viewProvider, setViewProvider] = useState(null);
    // Logout is owned by the console header now. clearing + dispatching the
    // shared event bounces the gate (which listens for it) back to its login.
    const handleLogout = () => {
        clearVoiceAdmin();
        window.dispatchEvent(new Event('voice-admin-unauthorized'));
    };
    const openCreate = () => setModal({ mode: 'create' });
    // The list is a SUMMARY now, so the form can no longer be populated from the
    // row — it fetches the provider's full record and fills in when it lands.
    const editingProviderId = modal.mode === 'edit' || modal.mode === 'duplicate' ? modal.providerId : null;
    const { data: editingProvider, isLoading: loadingEditing } = useAdminVoiceProvider(editingProviderId);
    const { data: testProvider } = useAdminVoiceProvider(testProviderId);
    const closeModal = () => setModal({ mode: 'closed' });
    const handleSave = (payload) => {
        const onSuccess = (verb) => {
            showToast({ message: `Provider ${verb}`, status: 'success' });
            closeModal();
        };
        const onError = (e) => {
            var _a, _b, _c;
            const apiErrors = (e === null || e === void 0 ? void 0 : e.errors) || ((_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors) || ((_c = e === null || e === void 0 ? void 0 : e.data) === null || _c === void 0 ? void 0 : _c.errors);
            showToast({
                message: Array.isArray(apiErrors) ? apiErrors.join('; ') : (e === null || e === void 0 ? void 0 : e.error) || (e === null || e === void 0 ? void 0 : e.message) || 'Save failed',
                status: 'error',
            });
        };
        if (modal.mode === 'edit') {
            updateMut.mutate({ providerId: modal.providerId, data: payload }, { onSuccess: () => onSuccess('updated'), onError });
        }
        else {
            // Duplicate is a CREATE with a pre-filled body.
            createMut.mutate(payload, { onSuccess: () => onSuccess('created'), onError });
        }
    };
    const handleDelete = (providerId) => {
        deleteMut.mutate(providerId, {
            onSuccess: () => {
                showToast({ message: 'Provider deleted', status: 'success' });
                setConfirmDelete(null);
            },
            onError: (e) => showToast({ message: (e === null || e === void 0 ? void 0 : e.message) || 'Delete failed', status: 'error' }),
        });
    };
    const saving = createMut.isPending || updateMut.isPending;
    return (_jsxs(_Fragment, { children: [_jsxs(AdminConsoleLayout, { sections: SECTIONS, activeKey: tab, onSelect: (k) => setTab(k), adminEmail: getVoiceAdminEmail(), onLogout: handleLogout, pageTitle: route.view === 'interview' ? route.slug : undefined, pageDescription: route.view === 'interview'
                    ? 'One interview, down to the models that ran behind it.'
                    : undefined, headerAction: tab === 'providers' ? (_jsx(Button, { variant: 'primary', size: 'sm', onClick: openCreate, rightIcon: _jsx(Plus, { className: 'h-4 w-4' }), children: "Add Provider" })) : undefined, children: [tab === 'interview-mode' && _jsx(InterviewModePanel, {}), tab === 'proctoring' && (_jsx("div", { className: 'flex h-full min-h-[520px] flex-col overflow-hidden rounded-2xl border border-dash-border bg-dash-card shadow-sm', children: _jsx(ProctoringTestPanel, {}) })), tab === 'vad' && (_jsx("div", { className: 'min-h-[520px] overflow-y-auto rounded-2xl border border-dash-border bg-dash-card shadow-sm', children: _jsx(VadTestPanel, {}) })), route.view === 'interview' ? (_jsx(InterviewDetailPage, { interviewSlug: route.slug, onBack: () => setRoute({ view: 'section', key: 'dashboard', org: '' }), onOpenOrganization: (org) => setRoute({ view: 'section', key: 'dashboard', org }) })) : (_jsxs(_Fragment, { children: [tab === 'dashboard' && (_jsx(DashboardPanel, { organizationId: route.view === 'section' ? route.org : '', onOrganizationChange: (org) => setRoute({ view: 'section', key: 'dashboard', org }), onOpenInterview: (slug) => setRoute({ view: 'interview', slug }) })), (tab === 'usage' || tab.startsWith('usage.')) && (_jsx(UsagePanel, { view: tab === 'usage' ? 'breakdown' : tab.split('.')[1], onOpenInterview: (slug) => setRoute({ view: 'interview', slug }) })), tab === 'models' && _jsx(ModelsPanel, {}), tab === 'pricing' && _jsx(PricingPanel, {}), tab === 'providers' && (_jsx(ProviderListPanel, { providers: providers !== null && providers !== void 0 ? providers : [], loading: isLoading, onView: setViewProvider, onEdit: (providerId) => setModal({ mode: 'edit', providerId }), onDuplicate: (providerId) => setModal({ mode: 'duplicate', providerId }), onTest: (p) => setTestProviderId(p.provider_id), onDelete: setConfirmDelete, onCreate: openCreate }))] }))] }), _jsx(ProviderDetailView, { providerId: viewProvider, onClose: () => setViewProvider(null), onEdit: (providerId) => {
                    setViewProvider(null);
                    setModal({ mode: 'edit', providerId });
                }, onDuplicate: (providerId) => {
                    setViewProvider(null);
                    setModal({ mode: 'duplicate', providerId });
                } }), modal.mode !== 'closed' && (_jsx(ProviderFormModal, { mode: modal.mode, initial: modal.mode === 'create' ? null : editingProvider !== null && editingProvider !== void 0 ? editingProvider : null, loading: modal.mode !== 'create' && loadingEditing, saving: saving, onSave: handleSave, onClose: closeModal }, modal.mode === 'create' ? 'create' : modal.providerId + modal.mode)), testProviderId && (_jsx(Modal, { isOpen: true, title: `Test provider — ${(testProvider === null || testProvider === void 0 ? void 0 : testProvider.label) || testProviderId}`, onClose: () => setTestProviderId(null), onSave: () => setTestProviderId(null), contentWidth: '!max-w-4xl', containerPadding: 'px-8 py-6', showSaveButton: false, cancelLabel: 'Close', children: _jsx(DashOverlay, { children: testProvider ? (_jsx(VoiceProviderTestPanel, { provider: testProvider })) : (_jsx("div", { className: 'flex justify-center py-16', children: _jsx(Loader2, { className: 'h-6 w-6 animate-spin text-dash-muted' }) })) }) })), confirmDelete && (_jsx(Modal, { isOpen: true, title: 'Delete voice provider', onClose: () => setConfirmDelete(null), onSave: () => handleDelete(confirmDelete), contentWidth: 'w-full max-w-md', containerPadding: 'px-6 py-5', children: _jsx(DashOverlay, { children: _jsxs("p", { className: 'text-sm text-dash-text', children: ["Delete provider ", _jsx("span", { className: 'font-semibold', children: confirmDelete }), "? Interviews already scheduled with it will fall back to the default provider."] }) }) }))] }));
};
export default VoiceProviderManagement;
